import { nextTick, onBeforeUnmount, shallowRef, type Ref } from 'vue'
import {
  CameraMode,
  createPotreeEngine,
  presets,
  type ControlMode,
  type EngineConfig,
  type Plugin,
  type PotreeEngine,
} from '@sharefe/share3d-engine'
import {
  isGpuTimingRequested,
  toGraphicsConfig,
  type ExampleGraphicsBackend,
} from '../utils/graphicsUrlConfig'

export interface UsePotreeEngineOptions {
  plugins?: Plugin[]
  background?: string
  pointBudget?: number
  fov?: number
  cameraMode?: CameraMode
  controlMode?: ControlMode
  renderMode?: EngineConfig['renderMode']
  /**
   * 后端请求，必须由 `useExampleGraphicsCard().requestedBackend` 显式传入。
   * Hook 不再自行解析 URL，避免同一页面出现第二处后端来源。
   */
  backend: ExampleGraphicsBackend
}

function isContainerReady(element: HTMLElement) {
  return element.clientWidth > 0 && element.clientHeight > 0
}

async function waitForContainerLayout(element: HTMLElement) {
  for (let attempt = 0; attempt < 10; attempt += 1) {
    if (isContainerReady(element)) {
      return true
    }

    await nextTick()
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => {
        resolve()
      })
    })
  }

  return isContainerReady(element)
}

export function usePotreeEngine(
  container: Ref<HTMLElement | null>,
  options: UsePotreeEngineOptions,
) {
  const {
    plugins,
    background = 'black',
    pointBudget = 1e7,
    fov = 60,
    cameraMode = CameraMode.Perspective,
    controlMode = 'orbit',
    renderMode,
  } = options

  const engine = shallowRef<PotreeEngine | null>(null)
  /** 调用方（图形控制卡）请求的后端；实际在建引擎后由 getGraphicsInfo().kind 回填。 */
  const requestedBackend = shallowRef<ExampleGraphicsBackend>(options.backend)
  const actualBackend = shallowRef<ExampleGraphicsBackend | null>(null)
  let resizeSyncRaf = 0
  let detachViewportSync: (() => void) | null = null
  let resizeObserver: ResizeObserver | null = null

  const cancelScheduledResize = () => {
    if (resizeSyncRaf !== 0) {
      window.cancelAnimationFrame(resizeSyncRaf)
      resizeSyncRaf = 0
    }
  }

  const teardownViewportSync = () => {
    cancelScheduledResize()
    detachViewportSync?.()
    detachViewportSync = null
  }

  const scheduleResizeSync = (target = engine.value) => {
    if (!target || resizeSyncRaf !== 0) {
      return
    }

    resizeSyncRaf = window.requestAnimationFrame(() => {
      resizeSyncRaf = 0
      target.resize()
    })
  }

  const setupViewportSync = (target: PotreeEngine) => {
    teardownViewportSync()

    const handleViewportMutation = () => {
      scheduleResizeSync(target)
    }

    if (typeof ResizeObserver !== 'undefined' && container.value) {
      resizeObserver = new ResizeObserver(() => {
        scheduleResizeSync(target)
      })
      resizeObserver.observe(container.value)
    }

    window.addEventListener('resize', handleViewportMutation)
    window.visualViewport?.addEventListener('resize', handleViewportMutation)

    let detachPixelRatioListener: (() => void) | null = null
    if (typeof window.matchMedia === 'function') {
      const bindPixelRatioListener = () => {
        detachPixelRatioListener?.()
        const query = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`)
        const handleChange = () => {
          scheduleResizeSync(target)
          bindPixelRatioListener()
        }
        query.addEventListener('change', handleChange)
        detachPixelRatioListener = () => {
          query.removeEventListener('change', handleChange)
        }
      }

      bindPixelRatioListener()
    }

    detachViewportSync = () => {
      resizeObserver?.disconnect()
      resizeObserver = null
      window.removeEventListener('resize', handleViewportMutation)
      window.visualViewport?.removeEventListener('resize', handleViewportMutation)
      detachPixelRatioListener?.()
      detachPixelRatioListener = null
    }

    scheduleResizeSync(target)
  }

  const init = async () => {
    const containerElement = container.value
    if (!containerElement) {
      console.error('[usePotreeEngine] container element is missing')
      return
    }

    const containerReady = await waitForContainerLayout(containerElement)
    if (!containerReady) {
      console.error('[usePotreeEngine] container size is still 0 before init')
      return
    }

    const config: EngineConfig = {
      container: containerElement,
      plugins: plugins ?? presets.full(),
      background,
      pointBudget,
      renderMode,
      graphics: toGraphicsConfig(requestedBackend.value, {
        gpuProfiling: isGpuTimingRequested(),
      }),
    }

    const eng = await createPotreeEngine(config)

    eng.camera.fov = fov
    eng.camera.setMode(cameraMode)
    eng.controls.setMode(controlMode)
    actualBackend.value = eng.getGraphicsInfo().kind as ExampleGraphicsBackend

    Reflect.set(window, 'engine', eng)

    engine.value = eng
    setupViewportSync(eng)
  }

  const dispose = () => {
    teardownViewportSync()
    const currentEngine = engine.value
    if (currentEngine) {
      if (Reflect.get(window, 'engine') === currentEngine) {
        Reflect.deleteProperty(window, 'engine')
      }
      currentEngine.dispose()
    }
    engine.value = null
    actualBackend.value = null
  }

  onBeforeUnmount(dispose)

  // 供双后端回归脚本驱动的内部探针：dispose + init 等价于一次完整 engine 创建/销毁循环，
  // 不改变页面业务逻辑，也不参与产品交互。
  Reflect.set(window, '__EXAMPLE_ENGINE__', {
    snapshot: () => ({
      backend: actualBackend.value,
      hasEngine: Boolean(engine.value),
      requestedBackend: requestedBackend.value,
    }),
    dispose,
    init,
  })
  onBeforeUnmount(() => {
    if (Reflect.get(window, '__EXAMPLE_ENGINE__')?.dispose === dispose) {
      Reflect.deleteProperty(window, '__EXAMPLE_ENGINE__')
    }
  })

  return { engine, init, dispose, requestedBackend, actualBackend }
}
